'use client';

import { type RefObject, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { noop } from '@interxfi/utilities/fn/no-op';
import type { Func } from '@interxfi/utilities/types/function';

const subscribeToVisibilityChange = (callback: Func) => {
  if (typeof document === 'undefined') {
    return noop;
  }

  document.addEventListener('visibilitychange', callback);

  return () => {
    document.removeEventListener('visibilitychange', callback);
  };
};

const getVisibilityChangeSnapshot = () => (typeof document === 'undefined' ? 'hidden' : document.visibilityState);

const getVisibilityChangeServerSnapshot = () => 'hidden';

export function useVisibilityChange() {
  const visibilityState = useSyncExternalStore(subscribeToVisibilityChange, getVisibilityChangeSnapshot, getVisibilityChangeServerSnapshot);

  return visibilityState === 'visible';
}

export function useIsElementVisible(ref: RefObject<HTMLElement | null>) {
  const observer = useRef<IntersectionObserver | null>(null);
  const [isVisible, setIsVisible] = useState(true);

  useLayoutEffect(() => {
    const observable = ref.current;

    if (!observer.current) {
      observer.current = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
        },
        { threshold: 0 },
      );
    }

    setIsVisible(observable ? observable.getBoundingClientRect().width > 0 && observable.getBoundingClientRect().height > 0 : false);

    // Start observing the target element
    if (observable) {
      observer.current.observe(observable);
    }

    // Cleanup function to stop observing when component unmounts or ref changes
    return () => {
      observer.current?.disconnect();
    };
  }, [ref.current]);

  return isVisible;
}
